/**
 * The Upload File modal (piece 5, decision 0249). It is the one screen every
 * file-system rule of Files, versions, sharing, audit arrives at, and the only
 * such screen. `File`, `FileType` and `ScanConfirm` are stubbed here and
 * declared in that piece; phase 1 builds the screen against their shapes and
 * performs no classification itself.
 */

/**
 * The record types a file type can be limited to; a type naming none applies
 * everywhere (decision 0299). 🔒 T385 (P9-3): the four document types a
 * customer signature or a customer purchase order is filed on join the list
 * (decision 1166), so a type filed on a document can be limited the same way
 * a type filed on a Company or a Person already could. 🔒 `AssetNode` joins
 * it for the asset's picture (decision 1291). 🔒 `Bill` and `PurchaseOrder`
 * join it for the files a Zoho bill and purchase order bring (decision 4804).
 */
export const FILE_TYPE_RECORD_TYPES = ['CompanyRecord', 'PersonRecord', 'LocationRecord', 'SiteRecord', 'Item', 'Deal', 'Lead', 'Message', 'Quote', 'SalesOrder', 'Invoice', 'Bill', 'PurchaseOrder', 'ChangeOrder', 'AssetNode'] as const;
export type FileTypeRecordType = (typeof FILE_TYPE_RECORD_TYPES)[number];

/**
 * Whether a file type applies to a record type: naming none applies
 * everywhere, naming some limits it to what it names (decision 0299). 🔒
 * The picker's offer and the upload service's refusal read this one rule
 * (decision 1190; T449), so a type the picker never offered against a
 * record is a type the server refuses too, and neither drifts from the
 * other.
 */
export function fileTypeApplies<T extends { appliesTo?: readonly string[] }>(row: T, recordType: string): boolean {
  return !row.appliesTo || row.appliesTo.length === 0 || row.appliesTo.includes(recordType);
}

/**
 * The types a record's upload offers: those naming it, and those naming
 * nothing. 🔒 A type the application writes or assigns itself is never offered
 * by a generic upload window (decision 2420); the place that asks for it sets it.
 */
export function fileTypesFor<T extends { appliesTo?: readonly string[]; isSystem?: boolean }>(rows: readonly T[], recordType: string): T[] {
  return rows.filter((row) => row.isSystem !== true && fileTypeApplies(row, recordType));
}

/** The types the untagged upload offers, the Documents Inbox's: those naming no record, less the system's own (decision 2420). */
export function untaggedFileTypes<T extends { appliesTo?: readonly string[]; isSystem?: boolean }>(rows: readonly T[]): T[] {
  return rows.filter((row) => row.isSystem !== true && (!row.appliesTo || row.appliesTo.length === 0));
}

/**
 * 🔒 The act that opened an upload, where the act already says what the file is (decision 7850; T3170): a picture
 * taken or chosen, or a receipt scanned. A drop zone opened from a Files tile names no act.
 */
export type UploadAct = 'photo' | 'receipt' | 'employment';

/** 🔒 The choice that reveals the rest of the types that apply, the list's last entry where an act cut it (decision 7850). */
export const OTHER_FILE_TYPE_CHOICE = 'other-file-type';

/** The receipt act's own type, the seeded Receipt (decision 1475), named here so the modals list imports no seed; matched ignoring case, as the standard forms are (T3194). */
const RECEIPT_TYPE_NAME = 'receipt';

/**
 * 🔒 The types an upload offers first and the rest *Other Type* reveals (decision 7850). A picture's own types are
 * those carrying an image standard; a receipt's is the Receipt. Both lists hold only types that apply to the record,
 * or that name none where the upload is tagged to no record, so the server's refusal (decision 1190) is unchanged.
 * An upload with no act, or an act whose own types none apply, offers everything that applies and reveals nothing.
 */
export function uploadActFileTypes<T extends { name: string; appliesTo?: readonly string[]; isSystem?: boolean; compressOnUpload?: boolean; isEmploymentForm?: boolean }>(rows: readonly T[], recordType: string | null, act: UploadAct | null): { offered: T[]; others: T[] } {
  const applying = recordType === null ? untaggedFileTypes(rows) : fileTypesFor(rows, recordType);
  // 🔒 Decision 9680: an employment form is offered by the Person page's Employment Documents alone, an HR act, and by no other upload.
  return splitByUploadAct(act === 'employment' ? applying : applying.filter((row) => row.isEmploymentForm !== true), act);
}

/** The same cut over a list already narrowed to the types that apply, as the phone's picture choice holds it. */
export function splitByUploadAct<T extends { name: string; compressOnUpload?: boolean; isEmploymentForm?: boolean }>(all: readonly T[], act: UploadAct | null): { offered: T[]; others: T[] } {
  if (act === null) return { offered: [...all], others: [] };
  // 🔒 Decision 9680: Employment Documents' own types are the employment forms.
  const own = all.filter((row) => (act === 'photo' ? row.compressOnUpload === true : act === 'employment' ? row.isEmploymentForm === true : row.name.trim().toLowerCase() === RECEIPT_TYPE_NAME));
  if (own.length === 0) return { offered: [...all], others: [] };
  return { offered: own, others: all.filter((row) => !own.includes(row)) };
}

/** The refusal's own sentence, naming the type and the record kind it does not apply to (T449). */
export function fileTypeNotApplicableRefusal(fileTypeName: string, recordType: string): string {
  return `${fileTypeName} does not apply to a ${recordType}.`;
}

export interface FileTypeRow {
  id: string;
  name: string;
  /** The class this file type names, where it names one. */
  documentClass: string | null;
  /** Timesheets, Receipts and Mileage Logs run the photo pipeline. */
  runsPhotoPipeline: boolean;
}

/** The seeded row the map falls back to, declared in The permission model. */
export interface ModuleDefaultDocumentClass {
  module: string;
  documentClass: string;
}

/**
 * The upload fails closed into this queue rather than into a wide default: a
 * default class of General would make "forgot to pick a type" the widest
 * setting in the system.
 */
export const NEEDS_CLASSIFICATION = 'NeedsClassification';

export type ClassSource = 'fileType' | 'record' | 'moduleDefault' | 'needsClassification';

export interface ResolvedClass {
  documentClass: string;
  source: ClassSource;
}

/**
 * The file's class is resolved, never chosen in the modal. Three sources are
 * tried in order, and failing all three the upload lands in the
 * Needs-classification queue. The modal shows what it became read-only, so the
 * person sees the answer without being asked a question they cannot answer.
 */
export function resolveDocumentClass(
  fileType: Pick<FileTypeRow, 'documentClass'> | null,
  recordDeclaredClass: string | null,
  moduleDefault: ModuleDefaultDocumentClass | null,
): ResolvedClass {
  if (fileType?.documentClass) return { documentClass: fileType.documentClass, source: 'fileType' };
  if (recordDeclaredClass) return { documentClass: recordDeclaredClass, source: 'record' };
  if (moduleDefault?.documentClass) return { documentClass: moduleDefault.documentClass, source: 'moduleDefault' };
  return { documentClass: NEEDS_CLASSIFICATION, source: 'needsClassification' };
}

/** The modal records no module stamp of its own; the map is the only reader of the module. */
export const WRITES_MODULE_STAMP = false;

/** `fileTypeId` is required and has no default: the picker offers no entry standing for "none". */
export function fileTypeMissing(fileTypeId: string | null): boolean {
  return fileTypeId === null || fileTypeId === '';
}

/**
 * A configured file type hands off to `ScanConfirm` and the modal does not
 * close on save: a person confirms rotation and crop before grayscale and
 * downsampling, because automated orientation in testing produced a wrong,
 * confident-looking guess.
 */
export function handsOffToScanConfirm(fileType: Pick<FileTypeRow, 'runsPhotoPipeline'> | null): boolean {
  return Boolean(fileType?.runsPhotoPipeline);
}

export function closesOnSave(fileType: Pick<FileTypeRow, 'runsPhotoPipeline'> | null): boolean {
  return !handsOffToScanConfirm(fileType);
}

export interface UploadDraft {
  fileTypeId: string | null;
  /** The record the upload sits on, which may declare a class of its own. */
  recordType: string;
  recordId: string;
  module: string;
}
